'use client';

import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  BarController,
  BarElement,
  Chart as ChartJS,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type {
  BarSeries,
  Category,
  LineSeries,
} from '@/sections/visual-strategy/types';
import {
  BAR_COLORS,
  formatAsOfDate,
  formatPercent,
  getFirstYearStartMs,
} from '@/sections/visual-strategy/visualStrategyUtils';

ChartJS.register(
  BarController,
  BarElement,
  LineController,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
);

type MixedChartType = 'bar' | 'line';

type BarChartPoint = {
  x: string | Date;
  y: [number, number];
};

type LineChartPoint = {
  x: string | Date;
  y: number;
};

type VisualStrategyChartProps = {
  line?: Category['historicalPerformanceCharts']['line'];
  bar?: Category['historicalPerformanceCharts']['bar'];
};

const X_AXIS_LABEL_ROTATION_BREAKPOINT = 1300;

const getBarAnchorDate = (
  value: string,
  lineStartDate?: string,
  lineEndDate?: string,
) => {
  const yearDate = new Date(value);

  if (Number.isNaN(yearDate.getTime())) {
    return value;
  }

  const year = yearDate.getFullYear();
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  const start = lineStartDate ? new Date(lineStartDate) : undefined;
  const end = lineEndDate ? new Date(lineEndDate) : undefined;
  const boundedStart =
    start &&
    !Number.isNaN(start.getTime()) &&
    start.getFullYear() === year &&
    start > yearStart
      ? start
      : yearStart;
  const boundedEnd =
    end &&
    !Number.isNaN(end.getTime()) &&
    end.getFullYear() === year &&
    end < yearEnd
      ? end
      : yearEnd;

  return new Date(
    boundedStart.getTime() +
      (boundedEnd.getTime() - boundedStart.getTime()) / 2,
  );
};

const getYScale = (
  selectedSeries?: LineSeries,
  selectedBarSeries?: BarSeries,
) => {
  const lineValues = (selectedSeries?.data ?? []).map((point) => point.value);
  const barValues = (selectedBarSeries?.data ?? []).map((point) => point.value);
  const highlightMax = selectedSeries?.highlights?.max?.value;
  const highlightMin = selectedSeries?.highlights?.min?.value;
  const highlightValues = [highlightMax, highlightMin].filter(
    (value): value is number =>
      typeof value === 'number' && Number.isFinite(value),
  );
  const allValues = [...lineValues, ...barValues, ...highlightValues];

  if (!allValues.length) {
    return {
      min: -10,
      max: 10,
      stepSize: 5,
    };
  }

  const rawMin = Math.min(...allValues, 0);
  const rawMax = Math.max(...allValues, 0);
  const range = Math.max(rawMax - rawMin, 1);
  const padding = Math.max(range * 0.08, 1);
  const paddedMin = rawMin - padding;
  const paddedMax = rawMax + padding;
  const visualRange = paddedMax - paddedMin;
  const stepSize =
    visualRange <= 20
      ? 5
      : visualRange <= 60
        ? 10
        : visualRange <= 120
          ? 20
          : 25;
  const min = Math.floor(paddedMin / stepSize) * stepSize;
  const max = Math.ceil(paddedMax / stepSize) * stepSize;

  return { min, max, stepSize };
};

const getXScaleBounds = (selectedSeries?: LineSeries) => {
  const linePoints = selectedSeries?.data ?? [];

  if (!linePoints.length) {
    return { min: undefined, max: undefined };
  }

  const min = getFirstYearStartMs(linePoints[0].date);
  const maxDate = new Date(linePoints[linePoints.length - 1].date);

  if (min === undefined || Number.isNaN(maxDate.getTime())) {
    return { min: undefined, max: undefined };
  }

  return {
    min,
    max: maxDate.getTime(),
  };
};

const buildChartData = (
  selectedSeries?: LineSeries,
  selectedBarSeries?: BarSeries,
): ChartData<MixedChartType, (BarChartPoint | LineChartPoint)[]> => {
  const linePoints = selectedSeries?.data ?? [];
  const barPoints = selectedBarSeries?.data ?? [];
  const lineStartDate = linePoints[0]?.date;
  const lineEndDate = linePoints[linePoints.length - 1]?.date;

  return {
    datasets: [
      {
        type: 'bar',
        label: 'Yearly performance',
        data: barPoints.map((point) => ({
          x: getBarAnchorDate(point.date, lineStartDate, lineEndDate),
          y: [0, point.value] as [number, number],
        })),
        backgroundColor: barPoints.map((point) =>
          point.value >= 0 ? BAR_COLORS.positive : BAR_COLORS.negative,
        ),
        borderRadius: 0,
        borderSkipped: false,
        barThickness: 'flex',
        maxBarThickness: 88,
        categoryPercentage: 1,
        barPercentage: 0.92,
        order: 10,
      },
      {
        type: 'line',
        label: 'Historical performance',
        data: linePoints.map((point) => ({
          x: point.date,
          y: point.value,
        })),
        borderColor: '#7D97FF',
        backgroundColor: 'rgba(36, 60, 92, 0.6)',
        fill: true,
        borderWidth: 4,
        tension: 0.28,
        pointRadius: 0,
        pointHoverRadius: 4,
        order: 0,
      },
    ],
  };
};

const createBarLabelPlugin = (
  selectedBarSeries?: BarSeries,
): Plugin<MixedChartType> => ({
  id: 'visual-strategy-bar-labels',
  afterDatasetsDraw: (chart) => {
    const datasetIndex = chart.data.datasets.findIndex(
      (dataset) => dataset.type === 'bar',
    );

    if (datasetIndex < 0) {
      return;
    }

    const meta = chart.getDatasetMeta(datasetIndex);
    const barValues = selectedBarSeries?.data ?? [];
    const { ctx } = chart;

    ctx.save();
    ctx.font = '600 12px sans-serif';
    ctx.textAlign = 'center';

    meta.data.forEach((barElement, index) => {
      const value = barValues[index]?.value;

      if (typeof value !== 'number') {
        return;
      }

      const props = barElement.getProps(
        ['x', 'y', 'base'],
        true,
      ) as unknown as {
        x: number;
        y: number;
        base: number;
      };

      const isPositive = value >= 0;
      const chartAreaBottom = chart.chartArea.bottom;
      const positiveLabelY = Math.min(props.y, props.base) - 10;
      const negativeLabelY = Math.min(
        Math.max(props.y, props.base) + 10,
        chartAreaBottom - 18,
      );
      ctx.fillStyle = '#FFFFFF';
      ctx.textBaseline = isPositive ? 'bottom' : 'top';
      ctx.fillText(
        formatPercent(value),
        props.x,
        isPositive ? positiveLabelY : negativeLabelY,
      );
    });

    ctx.restore();
  },
});

const VisualStrategyChart = ({ line, bar }: VisualStrategyChartProps) => {
  const selectedSeries = line?.series?.[0];
  const selectedBarSeries = bar?.series?.[0];
  const shouldRotateYearLabels = useMediaQuery(
    `(max-width: ${X_AXIS_LABEL_ROTATION_BREAKPOINT}px)`,
  );

  const chartData = useMemo(
    () => buildChartData(selectedSeries, selectedBarSeries),
    [selectedBarSeries, selectedSeries],
  );

  const yScale = useMemo(
    () => getYScale(selectedSeries, selectedBarSeries),
    [selectedBarSeries, selectedSeries],
  );

  const xScaleBounds = useMemo(
    () => getXScaleBounds(selectedSeries),
    [selectedSeries],
  );

  const chartOptions = useMemo<ChartOptions<MixedChartType>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              if (context.dataset.type === 'bar') {
                const raw = context.raw as { x: string; y: [number, number] };
                return formatPercent(raw.y[1]);
              }

              const raw = context.raw as { x: string; y: number };
              return formatPercent(raw.y);
            },
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          bounds: 'data',
          offset: false,
          min: xScaleBounds.min,
          max: xScaleBounds.max,
          time: {
            unit: 'year',
            displayFormats: {
              year: 'yyyy',
            },
          },
          grid: {
            display: false,
          },
          border: {
            color: 'rgba(255, 255, 255, 0.9)',
            width: 2,
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.75)',
            maxTicksLimit: 12,
            autoSkip: true,
            minRotation: shouldRotateYearLabels ? 50 : 0,
            maxRotation: shouldRotateYearLabels ? 50 : 0,
            padding: 12,
            font: {
              size: 12,
            },
          },
        },
        y: {
          position: 'left',
          min: yScale.min,
          max: yScale.max,
          grid: {
            color: 'rgba(255, 255, 255, 0.18)',
            lineWidth: 1,
          },
          border: {
            display: false,
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.75)',
            stepSize: yScale.stepSize,
            padding: 14,
            font: {
              size: 12,
            },
          },
        },
      },
      interaction: {
        mode: 'nearest',
        intersect: false,
      },
    }),
    [shouldRotateYearLabels, xScaleBounds, yScale],
  );

  const yearlyBarLabelPlugin = useMemo(
    () => createBarLabelPlugin(selectedBarSeries),
    [selectedBarSeries],
  );

  return (
    <article className="mt-8">
      <h4 className="text-base font-medium">{line?.title}</h4>

      <p className="mt-2 text-xs text-white/70">
        {line?.asOfDate ? `Stand: ${formatAsOfDate(line.asOfDate)}` : undefined}
      </p>

      <div className="mt-5 h-[360px] rounded-xl bg-transparent">
        <Chart
          type="bar"
          data={chartData}
          options={chartOptions}
          plugins={[yearlyBarLabelPlugin]}
        />
      </div>
    </article>
  );
};

export default VisualStrategyChart;
